import axios, { AxiosError, AxiosResponse } from 'axios'
import { useUserStore } from '../stores/user-store'
import { useRouter } from 'vue-router'

import { API_BASE_URL } from '@/config/api'
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000 // 25 minutes
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes

// Types
export interface LoginRequest {
  usernameOrEmail: string
  password: string
  rememberMe: boolean
}

export interface LoginResponse {
  token: string
}

export interface UserProfile {
  id: string
  userName: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  isActive: boolean
  createdAt: string
  lastLoginAt: string
  roles: string[]
  permissions: string[]
  groups: string[]
  profileImagePath?: string
  fingerprintEnabled?: boolean
  facialRecognitionEnabled?: boolean
}

export interface RefreshTokenRequest {
  token: string
}

export interface RefreshTokenResponse {
  token: string
}

class AuthService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  private refreshTimer: NodeJS.Timeout | null = null
  private sessionTimer: NodeJS.Timeout | null = null
  private lastActivity: number = Date.now()

  constructor() {
    this.setupActivityListeners()
    this.setupAxiosInterceptors()
  }

  // Setup activity listeners to track user activity
  private setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    events.forEach((event) => {
      document.addEventListener(
        event,
        () => {
          this.lastActivity = Date.now()
          this.resetSessionTimer()
        },
        true,
      )
    })
  }

  // Setup axios interceptors for automatic token handling
  private setupAxiosInterceptors() {
    // Request interceptor to add token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => {
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const newToken = await this.refreshToken()
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return this.api(originalRequest)
            }
          } catch (refreshError) {
            this.logout('Token refresh failed')
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      },
    )
  }

  // Login method - Updated endpoint
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // Updated to use new gateway endpoint
      const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login', credentials)
      const { token } = response.data

      // Store token and user info
      this.storeToken(token)
      this.startTokenRefresh()
      this.resetSessionTimer()

      // Get user profile
      await this.fetchUserProfile()

      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status
        if (status === 401) {
          throw new Error('Invalid credentials. Please check your username and password.')
        } else if (status === 400) {
          throw new Error('Please provide both username/email and password.')
        } else if (status === 403) {
          throw new Error('Account locked. Contact your administrator.')
        } else if (status === 429) {
          throw new Error('Too many attempts. Please wait before trying again.')
        } else if (status && status >= 500) {
          throw new Error('Server error. Please try again later.')
        } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
          throw new Error('Network error. Check your connection and try again.')
        }
      }
      throw new Error('Login failed. Please try again.')
    }
  }

  // Fetch user profile - Updated endpoint
  async fetchUserProfile(): Promise<UserProfile> {
    try {
      // Updated to use new gateway endpoint
      const response: AxiosResponse<UserProfile> = await this.api.get('/auth/profile')
      const profile = response.data

      // Update user store
      const userStore = useUserStore()
      userStore.changeUserName(profile.userName)
      userStore.changeEmail(profile.email)
      userStore.changeRole(profile.roles[0] || 'User')

      // Store additional user info
      localStorage.setItem('userId', profile.id)
      localStorage.setItem('userFirstName', profile.firstName)
      localStorage.setItem('userLastName', profile.lastName)
      localStorage.setItem('userPermissions', JSON.stringify(profile.permissions))
      localStorage.setItem('userGroups', JSON.stringify(profile.groups))
      if (profile.profileImagePath) {
        localStorage.setItem('profileImagePath', profile.profileImagePath)
        userStore.changeProfileImage(profile.profileImagePath)
      }

      return profile
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      throw error
    }
  }

  // Refresh token - Updated endpoint
  async refreshToken(): Promise<string | null> {
    try {
      const currentToken = this.getStoredToken()
      if (!currentToken) {
        return null
      }

      // Updated to use new gateway endpoint
      const response: AxiosResponse<RefreshTokenResponse> = await this.api.post('/auth/refresh-token', {
        token: currentToken,
      })

      const { token } = response.data
      this.storeToken(token)
      return token
    } catch (error) {
      console.error('Token refresh failed:', error)
      return null
    }
  }

  // Start automatic token refresh
  private startTokenRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }

    this.refreshTimer = setInterval(async () => {
      try {
        await this.refreshToken()
      } catch (error) {
        console.error('Automatic token refresh failed:', error)
        this.logout('Token refresh failed')
      }
    }, TOKEN_REFRESH_INTERVAL)
  }

  // Reset session timer
  private resetSessionTimer() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer)
    }

    this.sessionTimer = setTimeout(() => {
      const timeSinceLastActivity = Date.now() - this.lastActivity
      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        this.logout('Session timeout due to inactivity')
      }
    }, SESSION_TIMEOUT)
  }

  // Store token in localStorage
  private storeToken(token: string) {
    localStorage.setItem('authToken', token)
  }

  // Get stored token
  private getStoredToken(): string | null {
    return localStorage.getItem('authToken')
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getStoredToken()
    if (!token) {
      return false
    }

    try {
      // Basic token validation (you might want to add more sophisticated validation)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000

      if (payload.exp && payload.exp < currentTime) {
        this.logout('Token expired')
        return false
      }

      return true
    } catch (error) {
      console.error('Token validation failed:', error)
      this.logout('Invalid token')
      return false
    }
  }

  // Upload profile image
  async uploadProfileImage(payload: { imageData: string; imageFormat?: string }) {
    const response = await this.api.post('/auth/upload-profile-image', payload)
    const { imagePath } = response.data as { imagePath: string }
    if (imagePath) {
      localStorage.setItem('profileImagePath', imagePath)
      const userStore = useUserStore()
      userStore.changeProfileImage(imagePath)
    }
    return response.data
  }

  // Retrieve profile image as Blob
  async getProfileImage(userId: string): Promise<Blob> {
    const response = await this.api.get(`/auth/profile-image/${userId}`, { responseType: 'blob' })
    return response.data as Blob
  }

  // Logout method
  async logout(message?: string, router?: any): Promise<void> {
    try {
      // Clear timers
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
      if (this.sessionTimer) {
        clearTimeout(this.sessionTimer)
        this.sessionTimer = null
      }

      // Call logout endpoint if user is authenticated
      if (this.isAuthenticated()) {
        try {
          // Updated to use new gateway endpoint
          await this.api.post('/auth/logout')
        } catch (error) {
          console.error('Logout API call failed:', error)
        }
      }

      // Clear stored data
      localStorage.removeItem('authToken')
      localStorage.removeItem('userId')
      localStorage.removeItem('userFirstName')
      localStorage.removeItem('userLastName')
      localStorage.removeItem('userPermissions')
      localStorage.removeItem('userGroups')

      // Reset user store
      const userStore = useUserStore()
      userStore.changeUserName('')
      userStore.changeEmail('')
      userStore.changeRole('')

      // Show logout message if provided
      if (message) {
        console.log('Logout:', message)
      }

      // Redirect to login page
      if (router) {
        router.push({ name: 'login' })
      } else {
        window.location.href = '/login'
      }
    } catch (error) {
      console.error('Logout error:', error)
      // Force redirect even if logout fails
      window.location.href = '/login'
    }
  }

  // Get user permissions
  getUserPermissions(): string[] {
    try {
      const permissions = localStorage.getItem('userPermissions')
      return permissions ? JSON.parse(permissions) : []
    } catch (error) {
      console.error('Failed to parse user permissions:', error)
      return []
    }
  }

  // Check if user has specific permission
  hasPermission(permission: string): boolean {
    const permissions = this.getUserPermissions()
    return permissions.includes(permission)
  }

  // Get user groups
  getUserGroups(): string[] {
    try {
      const groups = localStorage.getItem('userGroups')
      return groups ? JSON.parse(groups) : []
    } catch (error) {
      console.error('Failed to parse user groups:', error)
      return []
    }
  }

  // Check if user is in specific group
  isInGroup(group: string): boolean {
    const groups = this.getUserGroups()
    return groups.includes(group)
  }

  // Get user role
  getUserRole(): string {
    return localStorage.getItem('userRole') || 'User'
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const userRole = this.getUserRole()
    return userRole === role || userRole === 'Admin' // Admin has all roles
  }

  // Cleanup method
  cleanup() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer)
    }
  }

  // Get API instance
  getApi() {
    return this.api
  }
}

export const authService = new AuthService()
